import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link, useNavigate } from 'react-router-dom';
import { authService } from '../../services/authService';
import { familyService } from '../../services/familyService';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Icon from '../../components/AppIcon';
import AppImage from '../../components/AppImage';
import { useTheme } from '../../contexts/ThemeContext';

const SignUp = () => {
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [familyName, setFamilyName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();

  const handleSignUp = async (e) => {
    e?.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password?.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      const { user } = await authService?.signUp(email, password, fullName);
      
      const familyGroupName = familyName || `${fullName?.split(' ')?.[0]}'s Family`;
      await familyService?.createFamily(familyGroupName, user?.id);
      
      const initials = fullName?.split(' ')?.map(n => n?.[0])?.join('')?.toUpperCase()?.substring(0, 2);
      await familyService?.addFamilyMember(user?.id, {
        userId: user?.id,
        displayName: fullName?.split(' ')?.[0],
        memberRole: 'Primary Admin',
        color: '#EF4444',
        initials: initials,
        familyRole: 'primary_admin'
      });

      setSuccess(true);
      setTimeout(() => {
        navigate('/signin');
      }, 3000);
    } catch (err) {
      setError(err?.message || 'Failed to create account. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className={`min-h-screen flex items-center justify-center px-4 ${
        isDarkMode 
          ? 'bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900' :'bg-gradient-to-br from-blue-50 via-violet-50 to-pink-50'
      }`}>
        <div className="max-w-md w-full">
          <div className="bg-card rounded-xl shadow-warm-xl p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon name="check" size={32} className="text-green-600" />
            </div>
            <h2 className="text-h3 font-heading text-foreground mb-2">
              Account Created Successfully!
            </h2>
            <p className="text-muted-foreground mb-4">
              Please check your email to verify your account.
            </p>
            <p className="text-sm text-muted-foreground">
              Redirecting to sign in...
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Sign Up - Starling Schedule</title>
      </Helmet>
      <div className="min-h-screen bg-gradient-to-br from-primary/5 via-background to-secondary/5 flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          <div className="bg-card rounded-xl shadow-warm-xl p-8">
            {/* Logo */}
            <div className="flex justify-center mb-6">
              <AppImage
                src="/assets/images/ad4cbaf4-dc2b-4059-a1d5-7af12fa38f76_copy-1767800870676.jpg"
                alt="Starling Family Planner Logo"
                className="w-24 h-24 object-contain"
              />
            </div>

            {/* Header */}
            <div className="text-center mb-8">
              <h1 className="text-h2 font-heading text-foreground mb-2">
                Create Your Family Account
              </h1>
              <p className="text-muted-foreground">
                Start organizing your family today
              </p>
            </div>

            {/* Error Message */}
            {error && (
              <div className="mb-6 p-4 bg-destructive/10 border border-destructive rounded-lg flex items-start gap-3">
                <Icon name="alert-circle" size={20} className="text-destructive flex-shrink-0 mt-0.5" />
                <p className="text-sm text-destructive">{error}</p>
              </div>
            )}

            {/* Sign Up Form */}
            <form onSubmit={handleSignUp} className="space-y-4 mb-6">
              <Input
                label="Full Name"
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e?.target?.value)}
                placeholder="John Doe"
                required
              />

              <Input
                label="Email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e?.target?.value)}
                placeholder="your@email.com"
                required
              />

              <Input
                label="Password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e?.target?.value)}
                placeholder="At least 6 characters"
                required
              />

              <Input
                label="Confirm Password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e?.target?.value)}
                placeholder="Re-enter your password"
                required
              />

              <Input
                label="Family Name (Optional)"
                type="text"
                value={familyName}
                onChange={(e) => setFamilyName(e?.target?.value)}
                placeholder="The Johnsons"
              />

              <Button
                type="submit"
                fullWidth
                disabled={loading}
                iconName={loading ? 'loader' : 'user-plus'}
              >
                {loading ? 'Creating Account...' : 'Create Account'}
              </Button>
            </form>

            {/* Link to Sign In */}
            <div className="text-center text-sm text-muted-foreground">
              Already have an account?{' '}
              <Link to="/signin" className="text-primary hover:underline font-medium">
                Sign In
              </Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SignUp;